import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, ArrowRight, XCircle } from 'lucide-react';
import { storeUserDetails } from '../services/UserService';
import { API_URL } from '../utility/constants';
import { toast } from 'react-toastify';

/**
 * SSO Complete - Callback page from GeniusSIS CAS SSO
 * Backend redirects here with:
 * - Success: ?token=...&user=... (user is JSON string)
 * - Error: ?error=... [&username=... for user_not_found]
 * Error codes: missing_ticket | sso_not_configured | cas_validation_failed | cas_auth_failed | user_not_found | sso_error
 */
const ERROR_MESSAGES = {
  missing_ticket: 'No SSO ticket was received. Please try signing in again.',
  sso_not_configured: 'SSO is not configured. Please contact your administrator.',
  cas_validation_failed: 'SSO validation failed. Please try again or sign in with your email and password.',
  cas_auth_failed: 'SSO authentication failed. Please try again or sign in with your email and password.',
  user_not_found: (username) => `Your account (${username || 'this email'}) is not registered in QLITEX. Please contact your school administrator.`,
  sso_error: 'An error occurred during sign-in. Please try again or use your email and password.',
};

function getRedirectPath(user) {
  const role = user?.role;
  const terms_and_conditions_accepted = user?.terms_and_conditions_accepted;
  const is_first_time_login = user?.is_first_time_login;
  const need_password_change = user?.need_password_change;

  if (role === 'ACCOUNT_OWNER') {
    if (!terms_and_conditions_accepted) return '/termsandconditions';
    if (is_first_time_login) return '/adminguidedtour';
    return '/admin/dashboard';
  }
  if (role === 'LEARNER') {
    if (!terms_and_conditions_accepted) return '/termsandconditions';
    if (need_password_change) return '/learner/passwordreset';
    if (is_first_time_login) return '/learnerguidedtour';
    return '/learner/dashboard';
  }
  if (role === 'SUPER_ADMIN') {
    if (is_first_time_login) return '/saguidedtour';
    return '/superadmin/dashboard';
  }
  return '/learner/dashboard';
}

export default function SSOComplete() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [redirecting, setRedirecting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const token = searchParams.get('token');
  const userParam = searchParams.get('user');
  const error = searchParams.get('error');
  const username = searchParams.get('username');

  const clearUrlParams = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    document.documentElement.style.setProperty('--primary-color', '#C13F63');
    document.documentElement.style.setProperty('--secondary-color', '#366494');
    document.documentElement.style.setProperty('--tertiary-color', '#58C181');
  }, []);

  useEffect(() => {
    if (error) {
      const msg = error === 'user_not_found'
        ? ERROR_MESSAGES.user_not_found(username ? decodeURIComponent(username) : null)
        : ERROR_MESSAGES[error] || ERROR_MESSAGES.sso_error;
      setErrorMessage(msg);
      setStatus('error');
      clearUrlParams();
      return;
    }

    if (token && userParam) {
      try {
        const user = JSON.parse(decodeURIComponent(userParam));
        const data = { user, token };

        // Store same format as Login.js - uses UserService.storeUserDetails
        storeUserDetails(data);
        toast.success('Sign-in successful!');
        clearUrlParams();

        const path = getRedirectPath(user);
        setStatus('success');
        setRedirecting(true);
        navigate(path, { replace: true });
      } catch (err) {
        console.error('SSO complete parse error:', err);
        setErrorMessage(ERROR_MESSAGES.sso_error);
        setStatus('error');
        clearUrlParams();
      }
    } else {
      // No token/user and no error - user landed here directly (e.g. bookmark)
      setStatus('error');
      setErrorMessage('No sign-in data received. Please start from the login page.');
    }
  }, [token, userParam, error, username, navigate, clearUrlParams]);

  const handleContinueToLogin = () => {
    navigate('/login', { replace: true });
  };

  const handleRetrySSO = () => {
    const launchUrl = `${API_URL.replace(/\/$/, '')}/sso/launch`;
    window.location.href = launchUrl;
  };

  // Loading state - brief moment while processing
  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#366494] border-t-transparent rounded-full animate-spin" />
          <p className="inter-500 text-gray-600">Completing sign-in...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div className="min-h-screen flex flex-wrap">
        <div className="w-full lg:w-1/2 min-h-[45vh] lg:min-h-screen relative overflow-hidden">
          <div
            className="h-full bg-center bg-no-repeat bg-cover flex items-center justify-center py-12 lg:py-16 px-8 lg:px-16"
            style={{ backgroundImage: "url('../../images/login-bg.webp')" }}
          >
            <div className="relative z-10 text-center lg:text-left">
              <a href="https://quantumliteracy.org" onClick={(e) => { e.preventDefault(); window.open('https://quantumliteracy.org', '_blank'); }} aria-label="qlite logo">
                <img tabIndex={0} className="cursor-pointer w-24 md:w-32 mx-auto lg:mx-0 brightness-0 mb-6" src="../images/qlite-logo.webp" alt="qlite logo" />
              </a>
              <h2 className="poppins-bold text-white leading-tight text-3xl md:text-4xl 2xl:text-5xl mb-4">
                Welcome to <br /><span className="pacifico-regular text-2xl md:text-3xl 2xl:text-4xl">QLITEX</span> E-Learning
              </h2>
              <p className="text-white/90 inter-400 text-base md:text-lg max-w-md mx-auto lg:mx-0">
                Quantum Literacy to Ignite Technology Education
              </p>
            </div>
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(135deg, rgba(54, 100, 148, 0.4) 0%, rgba(193, 63, 99, 0.3) 50%, rgba(88, 193, 129, 0.2) 100%)' }} />
          </div>
        </div>

        <div className="w-full lg:w-1/2 min-h-[55vh] lg:min-h-screen flex items-center justify-center bg-white">
          <div className="w-full max-w-lg mx-6 lg:mx-12 p-8 md:p-12 rounded-2xl shadow-xl" style={{ background: 'linear-gradient(148deg, #FFF 60%, #FEF7F7 100%)', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)' }}>
            <div className="flex justify-center mb-8">
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #E57373 0%, #EF5350 100%)', boxShadow: '0 10px 40px -10px rgba(239, 83, 80, 0.5)' }}>
                <XCircle className="w-14 h-14 md:w-16 md:h-16 text-white" strokeWidth={2} />
              </div>
            </div>
            <h1 className="text-2xl md:text-3xl inter-700 text-center mb-3" style={{ color: '#366494' }}>
              Sign-In Unsuccessful
            </h1>
            <p className="text-center inter-500 text-gray-600 mb-2 text-sm md:text-base">
              via <span className="font-semibold text-[#366494]">GeniusSIS</span>
            </p>
            <p className="text-center inter-400 text-gray-600 text-sm md:text-base mb-8">
              {errorMessage}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleContinueToLogin}
                className="flex-1 flex items-center justify-center gap-2 px-8 py-4 rounded-full inter-600 text-base text-white transition-all duration-300 button-shadow hover:shadow-lg"
                style={{ background: 'linear-gradient(135deg, #C13F63 0%, #B44D71 100%)' }}
                aria-label="Sign in with email"
              >
                Sign in with Email & Password
                <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
              </button>
              <button
                onClick={handleRetrySSO}
                className="flex-1 flex items-center justify-center gap-2 px-8 py-4 rounded-full inter-600 text-base border-2 border-[#366494] text-[#366494] hover:bg-[#366494] hover:text-white transition-all duration-300"
                aria-label="Try SSO again"
              >
                Try SSO Again
              </button>
            </div>
            <p className="text-center mt-6">
              <Link to="/" className="text-sm inter-500 text-[#366494] hover:text-[#C13F63] underline underline-offset-2">
                Return to Home
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success state (brief - we redirect quickly, but render in case of delay)
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="flex flex-col items-center gap-4">
        <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #58C181 0%, #47D28E 100%)' }}>
          <CheckCircle2 className="w-10 h-10 text-white" strokeWidth={2} />
        </div>
        <p className="inter-600 text-[#366494]">Redirecting you to your dashboard...</p>
        <div className="w-12 h-12 border-4 border-[#366494] border-t-transparent rounded-full animate-spin" />
      </div>
    </div>
  );
}
